<template>
  <section id="eps-container">
    <div id="menu">
      <eps-column-selector :active-columns="activeColumns" :available-columns="availableColumns"
                           :column-selector="columnSelector" :container="container" v-on:focus-column="setFocusCol"
                           v-on:launch="setColumnSelector" v-on:set-column="setActiveCols"/>
      <v-text-field v-model="search" apped-icon="search" label="Filter rows by text..."
                    single-line hide-details></v-text-field>
    </div>
    <v-data-table class="eps-table"  :class="{ scaled: columnSelector }"  :headers="dataColumns"
                  :items="permits" :search="search" :loading="loading" ref="container">
      <template v-slot:body="{ items }" v-bind:dataColumns="dataColumns">
        <tbody>
          <tr v-for="item in items">
            <td v-for="column in dataColumns" :class="{ 'focus-col': column.text === focusColumn }">
              <template v-if="Array.isArray(item[column.text]) && item[column.text].length === 1">
                {{item[column.text][0]}}
              </template>
              <template v-else-if="Array.isArray(item[column.text]) && item[column.text].length > 1">
                <ul :style="{ columnCount: calculateListCols(item[column.text].length)}">
                  <li v-for="text in item[column.text]" :key="text">
                    {{text}}
                  </li>
                </ul>
              </template>
              <template v-else-if="column.value === 'Public Files'">
                <v-dialog v-if="item['Public Files']">
                  <template v-slot:activator="{ on }">
                    <v-btn color="primary" v-on="on" v-on:click="loadFiles(item)">View Files</v-btn>
                  </template>
                  <v-card>
                    <div>
                      <v-card-title>
                        Public Files for {{selectedPermit}}
                      </v-card-title>
                      <v-card-text id="fileDialog" :class="{ loading: filesLoading }">
                        <v-progress-circular v-if="filesLoading" :indeterminate="true" color="primary"></v-progress-circular>
                        <ul class="files-list" v-if="!filesLoading">
                          <li v-for="(category, i) in files" :key="i">
                            <b v-if="category.contents.length">{{category.name}}</b>
                            <ul v-if="category.contents.length">
                              <li v-for="(file, i) in category.contents" :key="i">
                                <a :href="file">
                                  <v-icon v-text="'mdi-attachment'"></v-icon>{{file}}
                                </a>
                              </li>
                            </ul>
                          </li>
                        </ul>
                      </v-card-text>
                    </div>
                  </v-card>
                </v-dialog>
              </template>
              <template v-else>
                {{item[column.text]}}
              </template>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
  </section>
</template>

<script>
  import axios from 'axios';
  import moment from 'moment';
  import aprmFieldMap from "../static/columns-APRM.json";
  import aprmDefaults from "../static/columns-APRM-default.json";
  import EpsColumnSelector from "./EpsColumnSelector";

  export default {
    components: {EpsColumnSelector},
    data () {
      return {
        nodes: [],
        fields: [],
        permits: [],
        fieldMap: aprmFieldMap,
        columnSelector: false,
        activeColumns: this.columns.filter(column => column.default).map(column => column.label),
        availableColumns: this.columns.map(column => column.label),
        search: '',
        loading: true,
        scale: 1,
        container: {},
        focusColumn: false,
        files: {
          depot: {
            name: "File Depot",
            contents: []
          },
          shared: {
            name: "Shared Files",
            contents: []
          },
        },
        filesLoading: false,
        selectedPermit: '',
        currentNode: 0,
        batchSize: 10
      }
    },
    props: {
      columns: {
        type: Array,
        default: () => aprmDefaults
      },
      permittingAuthorities: {
        type: Array,
        default: () => []
      },
      env: {
        type: String,
        default: () => 'https://cdx.epa.gov'
      },
      filters: {
        type: Array,
        default: () => []
      }
    },
    computed: {
      dataColumns() {
        let cols = [];
        this.activeColumns.forEach(column => {
          cols.push({
            text: column,
            value: column,
            sortable: true,
            align: 'center'
          });
        });
        return cols;
      },
      machineFilters() {
        return this.filters.map(filter => {
          return {
            field: this.fieldMap[filter.field].flat()[0],
            values: filter.values
          }
        });
      }
    },
    methods: {
      getNodes () {
        this.permittingAuthorities.forEach( (permittingAuthority, index) => {
          axios.post(`${this.env}/get-permits-submitted`, {
            permitting_authority: permittingAuthority,
            filters: this.machineFilters
          })
          .then(response => {
            let existingNodes = this.nodes;
            let newNodes = Object.keys(response.data).map(function(key){ return response.data[key] });
            newNodes.forEach((node) => { node.processed = false });
            this.nodes = existingNodes.concat(newNodes);
            if (index === this.permittingAuthorities.length - 1) {
              this.getPermit();
            }
          })
          .catch(error => {
            console.log(`Couldn't load nodes: ${error.message}`);
          })
        });
      },
      getPermit () {
        for (let i = 1; i <= this.batchSize; i++) {
          let node = this.nodes[this.currentNode];
          axios.post(`${this.env}/get-permit-by-nid`, {
            nid: node.nid,
            fields: this.fields
          })
          .then(response => {
            if (response.data) {
              let permit = this.formatColumns(response.data);
              permit.nid = node.nid;
              this.permits.push(permit);
            }
            node.processed = true;
            if (this.currentNode < this.nodes.length && i === this.batchSize) {
              this.getPermit();
            }
          })
          .catch(error => {
            console.log(`Couldn't load permits: ${error.name}: ${error.message}`);
          });
          this.currentNode++;
        }
      },
      loadFiles (permit) {
        this.filesLoading = true;
        this.files.depot.contents = [];
        this.files.shared.contents = [];
        axios({
          method: 'get',
          url: `${this.env}/get-permit-files-by-nid`,
          params: {nid: permit.nid}
        })
        .then(response => {
          this.files.depot.contents = response.data.depot;
          this.files.shared.contents = response.data.shared;
          this.selectedPermit = permit["Facility Name"] ? `Facility: ${permit["Facility Name"]}` : "Permit";
        })
        .catch((error) => {
          this.files = {
            error: "Couldn't load files.",
            message: error
          }
        })
        .then(() => {
          this.filesLoading = false;
        });
      },
      formatColumns (fields) {
        let permit = {};
        let fieldMap = this.fieldMap;
        this.availableColumns.forEach(column => {
          let field = (fieldMap[column].length === 1)
                      ? fieldMap[column][0]
                      : fieldMap[column];
          switch(column){
            case 'Submittal Date':
            case 'End of EPA 45-Day Review':
            case 'Application Receipt Date':
            case 'Application Completeness Date':
            case 'Public Notice Period Start Date':
            case 'Public Notice Period End Date':
            case 'Final Permitting Authority Action Date':
            case 'Petition Period Start Date':
            case 'Petition Period End Date':
              permit[column] = (fields[field])
                               ? moment.unix(fields[field]).format('MM/DD/YYYY')
                               : null;
              break;
            case 'Permit Type':
            case 'Source Type':
              permit[column] = Array.isArray(fields[field])
                               ? Array.from(new Set(fields[field])) //remove duplicates
                               : fields[field];
              break;
            case 'Permitting Authority POC* Name':
            case 'EPA POC* Name':
              permit[column] = (Array.isArray(field))
                               ? field.map(subfield => fields[subfield]).join(' ')
                               : fields[field];
              break;
            case 'Project Number':
            case 'Application Number':
            case 'Facility SIC/NAICS Code(s)':
              permit[column] = (Array.isArray(fields[field]))
                               ? fields[field].join(' ')
                               : fields[field];
              break;
            case 'Expedited Review Requested':
              permit[column] = fields[field] === true ? '✓' : '';
              break;
            case 'Submission Category':
            case 'Title V Permit Action':
            case 'NSR Permit Action':
              // Remove empty elements from array:
              field = field.filter(subfield => fields[subfield]);
              // Concatenate if there are still multiple elements:
              permit[column] = field.length > 1
                    ? field.map(subfield => fields[subfield]).join(' - ')
                    : fields[field[0]];
              break;
            default:
              permit[column] = fields[field];
              break;
          }
        });
        return permit;
      },
      calculateListCols (liCount) {
        return Math.ceil(liCount/3);
      },
      setFocusCol(val) {
        this.focusColumn = val;
        // It's either this or use the header slot of v-data-table
        let headers = Array.from(document.querySelectorAll('th'));
        headers.forEach(header => {
          if (header.textContent === val) header.classList.add('focus-col');
          else                            header.classList.remove('focus-col');
        });
      },
      setColumnSelector(val) {
        this.columnSelector = val;
      },
      setActiveCols(val) {
        this.activeColumns = val;
      }
    },
    mounted () {
      // Initialize data which relies on mounting:
      this.container = this.$refs.container.$el;
      this.fields = this.availableColumns.map(field => this.fieldMap[field]).flat();
      // Start loading:
      this.getNodes();
      // Check for problematic webCMS container:
      let webCMS = document.querySelector('#main-content');
      let webCMSpos = webCMS ? window.getComputedStyle(webCMS).position : false;
      //If position is set to "relative", adjust position accordingly:
      if (webCMSpos === 'relative') {
        let pageInput = document.querySelector('.v-data-footer .v-input--hide-details .v-input__control');
        pageInput.addEventListener('click', function(){
          setTimeout(() => {
            let pageDropDown = document.querySelector('.v-menu__content.menuable__content__active');
            let screenTop =  window.pageYOffset || document.documentElement.scrollTop;
            let headerDistance = webCMS.getBoundingClientRect().top + screenTop;
            let inputOffset = this.getBoundingClientRect().top + screenTop - headerDistance;
            pageDropDown.style.top = inputOffset + "px";
          }, 50);
        });
      }
    },
  }
</script>
<style lang="scss">
  @import "../styles/webcms.scss";
  @import "../styles/vfy-overrides.scss";

  #eps-container {
    margin: 1%;
    box-shadow: 0px 0px 3px rgba(0,0,0,.1);
    padding: 15px;
    position: relative;

    .eps-table.v-data-table {
      background-color: transparent;
    }

    #menu {
      display: flex;
      justify-content: space-between;
      margin-bottom: 15px;
      align-items: center;

      .v-input {
        flex-grow: 0;
        flex-basis: 400px;
      }
    }
  }

  .eps-table {
    font-size: 12px;

    &.scaled .v-data-table__wrapper {
      overflow-x: hidden;
    }

    table {
      transform: scale(1);
      transform-origin: left top;
      transition: transform 150ms ease-in-out;
    }

    th, td {
      white-space: nowrap;
      background-color: transparent;
      transition: background-color 150ms ease-in-out;

      &.focus-col {
        background-color: rgba(25,118,210, .4);
        transition: background-color 150ms ease-in-out;
      }
    }

    th {
      position: relative;

      .v-icon {
        position: absolute;
        margin-left: 5px;
        z-index: 1;
      }
    }

    ul {
      text-align: left;
      font-size: 75%;
      list-style: none;
    }

    .v-data-footer {
      font-size: 140%;
    }
  }

  #fileDialog.loading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }
  ul.files-list {
    list-style: none;
    > li {
      margin-top: 5px;
      > b { font-size: 1.3em; }
      > ul {
        list-style: none;
        margin-bottom: 15px;
        > li {
          padding: 5px;
          background-color: transparent;
          transition: background-color 150ms ease-in-out;
          &:hover {
            background-color: #BBDEFB;
            transition: background-color 150ms ease-in-out;
          }
          > a {
            text-decoration: none;
            display: block;
            > i { padding-right: 10px; }
          }
        }
      }
    }
  }
</style>
